import { addNoScrollBody, removeNoScrollBody } from './no-scroll';

const addClickListener = (fn) => (el) => el.addEventListener('click', fn);
const removeClickListener = (fn) => (el) => el.removeEventListener('click', fn);
const initModals = () => {
  const modalEls = Array.from(document.querySelectorAll('[data-modal]'));
  modalEls.forEach((el) => Modal.create(el));
};

const modals = {};

// Modal accessibility reference: https://www.w3.org/TR/wai-aria-practices/#dialog_modal
class Modal {
  constructor(el) {
    if (!el || !(el instanceof HTMLElement) || typeof el.dataset.modal === 'undefined') {
      // must be an element with the data-modal attribute
      return;
    }

    this.el = el;
    this.id = el.id;
    this._initialContent = this.el.innerHTML;

    // init buttons
    const listenerPreventsDefault = (fn) => (e) => {
      e.preventDefault();
      fn.apply(this);
    };
    this._closeButtonEventListener = listenerPreventsDefault(this.close);
    this._openButtonEventListener = listenerPreventsDefault(this.open);
    this._toggleButtonEventListener = listenerPreventsDefault(this.toggle);
    this.initButtons();

    // add modal open event listener
    this.el.addEventListener('changeModalOpen', (e) => {
      // update body no-scroll
      this.isOpen ? addNoScrollBody() : removeNoScrollBody();

      // update modal hidden attr
      this.isOpen ?
        this.el.removeAttribute('hidden') :
        this.el.setAttribute('hidden', '');

      // update button classes & attr
      this.buttons.forEach((button) => {
        this.isOpen ?
          button.classList.add('open') :
          button.classList.remove('open');
        button.setAttribute('aria-expanded', this.isOpen.toString());
      });
    });

    // sync modal state from history and add popstate (back) event handler
    const syncFromState = (state) => this.isOpen = (state?.openModal === this.id);
    window.addEventListener('popstate', (e) => syncFromState(e.state));
    syncFromState(history.state);
  }

  get isOpen() {
    return this._isOpen;
  }

  set isOpen(isOpen) {
    this._isOpen = isOpen;
    // when open state changes, dispatch changeModalOpen event.
    this.el.dispatchEvent(new CustomEvent('changeModalOpen'));
  }

  get innerHTML() {
    return this.el.innerHTML;
  }

  set innerHTML(innerHTML) {
    this.el.innerHTML = innerHTML;
    this.initButtons();
  }

  get buttons() {
    return this._buttons;
  }

  set buttons(buttons) {
    // remove event listeners from old buttons
    this._closeButtons?.forEach(removeClickListener(this._closeButtonEventListener));
    this._openButtons?.forEach(removeClickListener(this._openButtonEventListener));
    this._toggleButtons?.forEach(removeClickListener(this._toggleButtonEventListener));

    // set new buttons
    this._buttons = [...new Set(buttons)]; // dedupe
    this._closeButtons = this.buttons.filter((button) => button.dataset.modalButton === 'close');
    this._openButtons = this.buttons.filter((button) => button.dataset.modalButton === 'open');
    this._toggleButtons = this.buttons.filter((button) => !(['open', 'close'].includes(button.dataset.modalButton)));

    // add event listeners to new buttons
    this._closeButtons.forEach(addClickListener(this._closeButtonEventListener));
    this._openButtons.forEach(addClickListener(this._openButtonEventListener));
    this._toggleButtons.forEach(addClickListener(this._toggleButtonEventListener));
  }

  open() {
    this.isOpen = true;
    history.pushState({ 'openModal': this.id }, '');
  }

  close() {
    history.back();
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  initButtons() {
    // first-time initialization of buttons/events, or re-init after HTML changes.
    this.buttons = Array.from(document.querySelectorAll(`[data-modal-button][data-target="#${this.id}"]`));
  }

  updateInnerHTML(contentHTML) {
    this.innerHTML = contentHTML;
    this.initButtons();
  }

  resetContentToInitial() {
    this.updateInnerHTML(this._initialContent);
  }

  clearContent() {
    this.updateInnerHTML('');
  }

  // Public API: create modals using Modal.create(el) where el is a modal element
  // video modals are modified via ./video-controls (removes modal contents on close for effective video stop)
  static create(el) {
    modals[el.id] = new Modal(el);
  }
};

export default initModals;

export {
  Modal,
  modals,
};
